import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useOfferMode } from "@nju/data-access-portal-offers";
import { redirectToOrchestratorState } from "@nju/feature-sales-orchestrator";
import { ErrorMessage, Input } from "@nju/form";
import { salesRoutes } from "@nju/routes";
import {
  A,
  BlockContent,
  BlockText,
  Button,
  ButtonsWrapper,
  CircleLoader,
  Heading,
  Separator,
} from "@nju/ui";
import { getFormSessionStorageOrQuery } from "@nju/utils";
import { registrationMachine } from "./registration.machine";
import { RegistrationConfirmCreateOrder } from "./RegistrationConfirmCreateOrder";

export function RegistrationForm() {
  const router = useRouter();
  const mode = useOfferMode();
  const { offerId } = React.useMemo(() => {
    return {
      offerId: getFormSessionStorageOrQuery("oferta", router.query),
    };
  }, [router.query]);

  const [current, send] = useMachine(registrationMachine, {
    context: {
      offerIds: offerId ? [offerId] : undefined,
      mode,
    },
    actions: {
      redirectToClientStatus: (_, event) => {
        if (event.type === "GO_TO_CLIENT_STATUS") {
          router.push(salesRoutes.statusKlienta.get());
        }
      },
      redirectToClientInProgress: (_, event) => {
        if (event.type === "CLIENT_LOCKED_BY_OTHER_AGENT") {
          router.push(salesRoutes.klientObslugiwany.get());
        }
      },
      redirectToAnotherProcessInProgress: (_, event) => {
        if (event.type === "ANOTHER_ORDER_IN_PROGRESS") {
          router.push(salesRoutes.zamowienieWTrakcie.get());
        }
      },
      redirectToOrchestratorState: (_, event) => {
        if (
          event.type === "REGISTRATION_SUCCESSFUL" &&
          event.orchestrator &&
          event.orchestrator.type === "NJU_FTTH_ONBOARDING"
        ) {
          redirectToOrchestratorState({
            state: event.orchestrator.state,
            mode,
          });
        }
        if (
          event.type === "REGISTRATION_SUCCESSFUL" &&
          event.orchestrator &&
          event.orchestrator.type === "NJU_MULTIORDER_ONBOARDING"
        ) {
          router.push(salesRoutes.wybierak.get({ mode: "mobile" }));
        }
      },
      redirectToCustomerStatusPanel: (_, event) => {
        if (event.type === "GO_TO_MULTI_CLIENT_STATUS") {
          router.push(salesRoutes.statusKlienta.get());
        }
      },
    },
  });

  const { context, matches } = current;
  const isLoading = !matches("ready");

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send],
    );

  const handleEmailChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        send({ type: "SET_INPUT_VALUE", name: "email", value: target.value });
      },
      [send],
    );

  const handleCreateOrder = React.useCallback(() => {
    send({ type: "CREATE_ORDER" });
  }, [send]);

  if (matches("checkClientInstalled") || matches("createOrder")) {
    return (
      <div className="flex justify-center">
        <BlockContent border="none" rounded="primary" spacing="l" tone="none">
          <div className="flex justify-center items-center h-full">
            <CircleLoader />
          </div>
        </BlockContent>
      </div>
    );
  }

  if (matches("pendingCancellation")) {
    return (
      <RegistrationConfirmCreateOrder handleCreateOrder={handleCreateOrder} />
    );
  }

  return (
    <>
      <div data-cy="main-content">
        <Heading
          className="mb-6"
          font="ttfirs"
          tone="neutral"
          variant="h2"
          weight="bold"
        >
          załóż konto nju
        </Heading>
        <BlockText
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "l",
          }}
        >
          Tworzysz konto klienta dla usług <strong>subskrypcji</strong> i{" "}
          <strong>światłowodu</strong>.
        </BlockText>
        <BlockText
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "l",
          }}
          tone="accent"
        >
          Jeśli klient posiada konto dla usług{" "}
          <strong>abonament, na kartę, internet mobilny,</strong>
        </BlockText>
        <BlockText
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "l",
          }}
          tone="accent"
        >
          poinformuj go o tym i wyjaśnij, że to dwa różne konta.
        </BlockText>
        <BlockText
          className="mt-6"
          size={{
            "@onlyMobile": "s",
            "@fromTablet": "l",
          }}
        >
          Załóż bezpłatne konto, aby móc kontynuować.
        </BlockText>
        <form autoComplete="false" onSubmit={handleSubmit}>
          <div className="from-tablet:max-w-[400px]">
            <Separator size="s" tone="invisible" />
            <Input
              autoFocus={true}
              label="podaj e-mail"
              name="email"
              onChange={handleEmailChange}
              value={context.email}
            />
            {context.formError && (
              <>
                <Separator size="xxs" tone="invisible" />
                <ErrorMessage message={context.formError} />
              </>
            )}
            <Separator size="m" tone="invisible" />
          </div>
          <ButtonsWrapper>
            <Link
              href={
                mode === "mobile"
                  ? salesRoutes.panel.get()
                  : salesRoutes.wybierzOferte.get()
              }
              passHref={true}
            >
              <A size={{ "@onlyMobile": "s" }} variant="secondary">
                powrót
              </A>
            </Link>
            <Button
              isLoading={isLoading}
              size={{ "@onlyMobile": "s" }}
              type="submit"
            >
              załóż konto
            </Button>
          </ButtonsWrapper>
        </form>
      </div>
    </>
  );
}
